import { buildAiPlannerHotelSearchDataFromSearchParams } from "@/feature/ai-planner/lib/chat-hotel.utils";
import {
  isSeeMoreFlightsAction,
  isSeeMoreHotelsAction,
  type AiChatAction,
  type AiChatPayload,
  type AiSeeMoreFlightsAction,
  type AiSeeMoreFlightsActionPayload,
  type AiSeeMoreHotelsAction,
  type AiSeeMoreHotelsActionPayload,
} from "@/feature/ai-planner/types/ai-chat.types";
import { normalizeAssistantMessageMetadata } from "@/feature/ai-planner/lib/chat-message.utils";
import type { TAirport } from "@/feature/flight/search/types/search";
import { serializeFlightSearchParams } from "@/feature/flight/search/params/flight-search-params.client";
import { encodedHotelParams } from "@/feature/hotel/search/lib/hotel-formatter-helper";
import type { ChatMessage } from "../types/chat-ui.types";

export function createClientId(prefix: string) {
  if (
    typeof globalThis.crypto !== "undefined" &&
    globalThis.crypto.randomUUID
  ) {
    return `${prefix}-${globalThis.crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createSessionId() {
  return createClientId("session");
}

export function buildAssistantMessage(payload: AiChatPayload): ChatMessage {
  const metadata = normalizeAssistantMessageMetadata(payload);

  return {
    id: createClientId("assistant"),
    role: "assistant",
    text: payload.message,
    animateText: !payload.meta?.cached,
    type: payload.type,
    payload: payload.data,
    actions: payload.actions,
    highlightPhrases: metadata.highlightPhrases.length
      ? metadata.highlightPhrases
      : undefined,
    requiredFields: metadata.requiredFields.length
      ? metadata.requiredFields
      : undefined,
    suggestedQueries: metadata.suggestedQueries.length
      ? metadata.suggestedQueries
      : undefined,
    clarification: metadata.clarification,
    intent: payload._meta?.intent ?? payload.meta?.intent,
    waitingForUser: payload._meta?.waiting_for_user,
    createdAt: Date.now(),
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") return null;
  return value as Record<string, unknown>;
}

function asString(value: unknown) {
  return typeof value === "string" ? value : "";
}

function asNonNegativeInteger(value: unknown, fallback = 0) {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.max(0, Math.floor(value));
}

function asValidDepartDate(value: unknown) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  const date = asString(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : undefined;
}

function normalizeAirport(value: unknown): TAirport | null {
  const airport = asRecord(value);
  if (!airport) return null;

  const iataCode = asString(airport.iata_code).trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(iataCode)) return null;

  return {
    iata_code: iataCode,
    name: asString(airport.name),
    city_name: asString(airport.city_name),
    country: asString(airport.country),
  };
}

function getFlightSearchAction(
  action: AiChatAction,
): AiSeeMoreFlightsAction | null {
  return isSeeMoreFlightsAction(action) ? action : null;
}

function getHotelSearchAction(action: AiChatAction): AiSeeMoreHotelsAction | null {
  return isSeeMoreHotelsAction(action) ? action : null;
}

function getFlightSearchParams(
  payload: AiSeeMoreFlightsActionPayload | undefined,
) {
  return payload?.search_params;
}

function getHotelSearchParams(payload: AiSeeMoreHotelsActionPayload | undefined) {
  return payload?.search_params;
}

export function buildFlightRedirectPath(action: AiChatAction) {
  const flightAction = getFlightSearchAction(action);
  if (!flightAction) return null;

  const searchParams = getFlightSearchParams(flightAction.payload);
  if (!searchParams) return null;

  const tripTypeRaw = searchParams.tripType?.toLowerCase();
  const trip: "oneway" | "roundtrip" | "multicity" =
    tripTypeRaw === "roundtrip" || tripTypeRaw === "multicity"
      ? tripTypeRaw
      : "oneway";

  const passengers = searchParams.passengers;
  const pax = {
    adults: Math.max(1, asNonNegativeInteger(passengers?.adults, 1)),
    children: asNonNegativeInteger(passengers?.children, 0),
    infants: asNonNegativeInteger(passengers?.infants, 0),
  };

  const cabinRaw = searchParams.cabin;
  const cabin: "1" | "2" | "3" | "4" =
    cabinRaw === "2" || cabinRaw === "3" || cabinRaw === "4" ? cabinRaw : "1";

  const ages = Array.isArray(searchParams.childrenAges)
    ? searchParams.childrenAges
        .filter((age): age is number => typeof age === "number")
        .filter((age) => Number.isFinite(age) && age >= 0 && age <= 17)
        .map((age) => Math.floor(age))
    : [];

  const route = Array.isArray(searchParams.flights)
    ? searchParams.flights.reduce<
        Array<{ from: TAirport; to: TAirport; departDate?: string }>
      >((accumulator, flightValue) => {
        const flight = asRecord(flightValue);
        if (!flight) return accumulator;

        const from = normalizeAirport(flight.from);
        const to = normalizeAirport(flight.to);
        if (!from || !to || from.iata_code === to.iata_code) {
          return accumulator;
        }

        const departDate = asValidDepartDate(flight.departDate);

        accumulator.push({ from, to, departDate });
        return accumulator;
      }, [])
    : [];

  if (!route.length) return null;

  const params = serializeFlightSearchParams({
    trip,
    route,
    pax,
    cabin,
    ages,
  });

  return `/flights${params}`;
}

export function buildHotelRedirectPath(action: AiChatAction) {
  const hotelAction = getHotelSearchAction(action);
  if (!hotelAction) return null;

  const searchParams = getHotelSearchParams(hotelAction.payload);
  const searchData =
    buildAiPlannerHotelSearchDataFromSearchParams(searchParams);

  if (!searchData) return null;

  return `/hotels?${encodedHotelParams(searchData)}`;
}

export function getActionFallbackMessage(action: AiChatAction) {
  const label = action.label?.trim();
  if (label) return label;
  if (action.type === "SEE_MORE_FLIGHTS") return "Show more flights";
  if (action.type === "SEE_MORE_HOTELS") return "Show more hotels";
  return "Show more options";
}
